import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/seo/seo-head";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Plus, Edit, Trash2, Target, TrendingUp, CreditCard, CheckCircle, Clock, AlertTriangle, PiggyBank, Calendar } from "lucide-react";
import { showToast } from "@/lib/toast";
import { Skeleton } from "@/components/shared/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrency } from "@/hooks/use-currency";
import { useLanguage } from "@/contexts/language-context";

interface BudgetGoal {
  id: string;
  goalName: string;
  amount: number;
  targetDate: string;
}

interface GoalProgress {
  progress: number;
  status: "not_started" | "in_progress" | "completed";
  currentAmount: number;
  targetAmount: number;
  daysRemaining: number;
}

interface DebtProgress {
  totalUtang: number;
  totalDibayar: number;
  sisaUtang: number;
  progressPercentage: number;
  byDebt: Array<{
    debtId: string;
    category: string;
    originalAmount: number;
    currentAmount: number;
    paid: number;
    progressPercentage: number;
  }>;
}

interface NetWorthTarget {
  targetAmount: number;
  currentAmount: number;
  targetDate: string;
}

export function GoalsPage() {
  const { formatCurrency } = useCurrency();
  const { t, language } = useLanguage();
  const [goals, setGoals] = useState<BudgetGoal[]>([]);
  const [goalProgress, setGoalProgress] = useState<Record<string, GoalProgress>>({});
  const [debtProgress, setDebtProgress] = useState<DebtProgress | null>(null);
  const [netWorthData, setNetWorthData] = useState<{
    current: number;
    target: number | null;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<BudgetGoal | null>(null);
  const [showNetWorthForm, setShowNetWorthForm] = useState(false);
  const [netWorthTarget, setNetWorthTarget] = useState<NetWorthTarget | null>(null);

  // Goal form state
  const [goalForm, setGoalForm] = useState({
    goalName: "",
    amount: "",
    targetDate: "",
  });

  // Net worth target form state
  const [netWorthForm, setNetWorthForm] = useState({
    targetAmount: "",
    targetDate: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [goalsData, debtProgressData, netWorthData] = await Promise.all([
        api.get<{ goals: BudgetGoal[] }>("/budget-goals"),
        api.get<{ result: DebtProgress }>("/calculations/debt-progress").catch(() => null),
        api.get<{ result: { netWorth: number } }>("/calculations/net-worth").catch(() => null),
      ]);

      setGoals(goalsData.goals);

      // Fetch progress for each goal
      const progressPromises = goalsData.goals.map(async (goal) => {
        try {
          const progressData = await api.get<{ progress: GoalProgress }>(
            `/budget-goals/${goal.id}/progress`
          );
          return { goalId: goal.id, progress: progressData.progress };
        } catch (error) {
          console.error(`Failed to fetch progress for goal ${goal.id}:`, error);
          return null;
        }
      });

      const progressResults = await Promise.all(progressPromises);
      const progressMap: Record<string, GoalProgress> = {};
      progressResults.forEach((result) => {
        if (result) {
          progressMap[result.goalId] = result.progress;
        }
      });
      setGoalProgress(progressMap);

      // Set debt progress
      if (debtProgressData) {
        setDebtProgress(debtProgressData.result);
      }

      // Set net worth data and check for net worth target goal
      if (netWorthData) {
        const currentNetWorth = netWorthData.result.netWorth;
        // Check if there's a net worth goal (goal name contains "Net Worth" or similar)
        const netWorthGoal = goalsData.goals.find(
          (g) => g.goalName.toLowerCase().includes("net worth") || g.goalName.toLowerCase().includes("networth")
        );
        
        setNetWorthData({
          current: currentNetWorth,
          target: netWorthGoal ? netWorthGoal.amount : null,
        });

        if (netWorthGoal) {
          setNetWorthTarget({
            targetAmount: netWorthGoal.amount,
            currentAmount: currentNetWorth,
            targetDate: netWorthGoal.targetDate,
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch goals:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingGoal) {
        await api.put(`/budget-goals/${editingGoal.id}`, {
          goalName: goalForm.goalName,
          amount: parseFloat(goalForm.amount),
          targetDate: goalForm.targetDate,
        });
      } else {
        await api.post("/budget-goals", {
          goalName: goalForm.goalName,
          amount: parseFloat(goalForm.amount),
          targetDate: goalForm.targetDate,
        });
      }
      setShowGoalForm(false);
      setEditingGoal(null);
      setGoalForm({ goalName: "", amount: "", targetDate: "" });
      showToast.success(editingGoal ? t("goals.goalUpdated") : t("goals.goalAdded"));
      fetchData();
    } catch (error) {
      console.error("Failed to save goal:", error);
      showToast.error(error instanceof Error ? error.message : t("common.error"));
    }
  };

  const handleNetWorthTargetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const goalName = t("goals.netWorthGrowth");
      const amount = parseFloat(netWorthForm.targetAmount);
      const targetDate = netWorthForm.targetDate;

      // Check if net worth goal already exists
      const existingGoal = goals.find(
        (g) => g.goalName.toLowerCase().includes("net worth") || g.goalName.toLowerCase().includes("networth")
      );

      if (existingGoal) {
        await api.put(`/budget-goals/${existingGoal.id}`, {
          goalName,
          amount,
          targetDate,
        });
        showToast.success(t("settings.success"));
      } else {
        await api.post("/budget-goals", {
          goalName,
          amount,
          targetDate,
        });
        showToast.success(t("settings.success"));
      }

      setShowNetWorthForm(false);
      setNetWorthForm({ targetAmount: "", targetDate: "" });
      fetchData();
    } catch (error) {
      console.error("Failed to save net worth target:", error);
      showToast.error(error instanceof Error ? error.message : t("common.error"));
    }
  };

  const handleDeleteGoal = async (id: string) => {
    if (!confirm(t("goals.deleteGoalConfirm"))) return;
    try {
      await api.delete(`/budget-goals/${id}`);
      showToast.success(t("goals.goalDeleted"));
      fetchData();
    } catch (error) {
      console.error("Failed to delete goal:", error);
      showToast.error(t("common.error"));
    }
  };

  const handleEditGoal = (goal: BudgetGoal) => {
    setEditingGoal(goal);
    setGoalForm({
      goalName: goal.goalName,
      amount: goal.amount.toString(),
      targetDate: new Date(goal.targetDate).toISOString().slice(0, 10),
    });
    setShowGoalForm(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === "id" ? "id-ID" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-primary";
      case "in_progress":
        return "bg-chart-2";
      default:
        return "bg-muted-foreground/40";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return t("goals.completedStatus");
      case "in_progress":
        return t("goals.inProgress");
      default:
        return t("goals.notStarted");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Goals & Debts - Personal Finance App"
        description="Set and track your financial goals. Manage your debts and monitor your progress towards achieving your financial objectives."
        keywords="financial goals, debt management, savings goals, budget goals, debt tracking"
        canonicalUrl="/goals"
      />
      <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("goals.title")}</h1>
        <p className="text-muted-foreground">
          {t("goals.subtitle")}
        </p>
      </div>

      {/* Goals Summary */}
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="h-8 w-8 md:h-9 md:w-9 rounded-lg bg-chart-1/10 flex items-center justify-center shrink-0">
                <Target className="h-4 w-4 text-chart-1" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-muted-foreground truncate">{t("goals.totalGoals")}</p>
                <p className="text-lg md:text-xl font-bold tabular-nums font-mono">{goals.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="h-8 w-8 md:h-9 md:w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <CheckCircle className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-muted-foreground truncate">{t("goals.completed")}</p>
                <p className="text-lg md:text-xl font-bold text-primary tabular-nums font-mono">
                  {Object.values(goalProgress).filter((p) => p.status === "completed").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="h-8 w-8 md:h-9 md:w-9 rounded-lg bg-chart-2/10 flex items-center justify-center shrink-0">
                <Clock className="h-4 w-4 text-chart-2" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-muted-foreground truncate">{t("goals.inProgress")}</p>
                <p className="text-lg md:text-xl font-bold text-chart-2 tabular-nums font-mono">
                  {Object.values(goalProgress).filter((p) => p.status === "in_progress").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different goal types */}
      <Tabs defaultValue="budget-goals" className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl w-full overflow-x-auto no-scrollbar">
          <TabsTrigger value="budget-goals" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">{t("goals.budgetGoals")}</TabsTrigger>
          <TabsTrigger value="debt-reduction" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">{t("goals.debtReduction")}</TabsTrigger>
          <TabsTrigger value="net-worth" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm whitespace-nowrap">{t("goals.netWorthGrowth")}</TabsTrigger>
        </TabsList>

        {/* Budget Goals Tab */}
        <TabsContent value="budget-goals">
          <div className="space-y-4">
            {/* Add Goal Button */}
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setEditingGoal(null);
                  const today = new Date();
                  const oneYearLater = new Date(today);
                  oneYearLater.setFullYear(today.getFullYear() + 1);
                  setGoalForm({
                    goalName: "",
                    amount: "",
                    targetDate: oneYearLater.toISOString().slice(0, 10),
                  });
                  setShowGoalForm(!showGoalForm);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("goals.addGoal")}
              </Button>
            </div>

            {/* Goal Form */}
            {showGoalForm && (
              <Card>
                <CardContent className="pt-6">
                  <form onSubmit={handleGoalSubmit} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="goal-name">{t("goals.goalName")}</Label>
                        <Input
                          id="goal-name"
                          value={goalForm.goalName}
                          onChange={(e) => setGoalForm({ ...goalForm, goalName: e.target.value })}
                          placeholder="e.g., Emergency Fund"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="goal-amount">{t("goals.targetAmount")}</Label>
                        <Input
                          id="goal-amount"
                          type="number"
                          step="0.01"
                          value={goalForm.amount}
                          onChange={(e) => setGoalForm({ ...goalForm, amount: e.target.value })}
                          placeholder="0.00"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="goal-date">{t("goals.targetDate")}</Label>
                        <Input
                          id="goal-date"
                          type="date"
                          value={goalForm.targetDate}
                          onChange={(e) => setGoalForm({ ...goalForm, targetDate: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit">
                        {editingGoal ? t("common.update") : t("common.add")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowGoalForm(false);
                          setEditingGoal(null);
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Goals List */}
            {goals.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Target className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-muted-foreground">{t("goals.noGoals")}</p>
                    <p className="text-sm text-muted-foreground mt-1">Create your first financial goal to start tracking progress</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {goals.map((goal) => {
                  const progress = goalProgress[goal.id];
                  const progressPercent = progress ? progress.progress : 0;
                  const status = progress?.status || "not_started";

                  return (
                    <Card key={goal.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          {/* Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                                status === "completed" ? "bg-primary/10" :
                                status === "in_progress" ? "bg-chart-2/10" : "bg-muted"
                              }`}>
                                <Target className={`h-5 w-5 ${
                                  status === "completed" ? "text-primary" :
                                  status === "in_progress" ? "text-chart-2" : "text-muted-foreground"
                                }`} />
                              </div>
                              <div>
                                <h3 className="font-semibold">{goal.goalName}</h3>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  status === "completed" ? "bg-primary/10 text-primary" :
                                  status === "in_progress" ? "bg-chart-2/10 text-chart-2" : "bg-muted text-muted-foreground"
                                }`}>
                                  {getStatusText(status)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleEditGoal(goal)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteGoal(goal.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Amounts */}
                          <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/50">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">{t("goals.targetAmount")}</p>
                              <p className="font-semibold tabular-nums font-mono">{formatCurrency(goal.amount)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">{t("goals.current")}</p>
                              <p className="font-semibold tabular-nums font-mono">{formatCurrency(progress?.currentAmount || 0)}</p>
                            </div>
                          </div>

                          {/* Progress */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">{t("goals.progress")}</span>
                              <span className="text-sm font-semibold tabular-nums font-mono">{progressPercent.toFixed(1)}%</span>
                            </div>
                            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${getStatusColor(status)}`}
                                style={{ width: `${Math.min(progressPercent, 100)}%` }}
                              />
                            </div>
                          </div>

                          {/* Meta */}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(goal.targetDate)}
                            </span>
                            {progress && progress.daysRemaining > 0 && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {progress.daysRemaining} days left
                              </span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Debt Reduction Plan Tab */}
        <TabsContent value="debt-reduction">
          {debtProgress ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid gap-4 sm:grid-cols-3">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("goals.totalDebt")}</p>
                        <p className="text-xl font-bold text-destructive tabular-nums font-mono">
                          {formatCurrency(debtProgress.totalUtang)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <CheckCircle className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("goals.paid")}</p>
                        <p className="text-xl font-bold text-primary tabular-nums font-mono">
                          {formatCurrency(debtProgress.totalDibayar)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-chart-2/10 flex items-center justify-center shrink-0">
                        <PiggyBank className="h-5 w-5 text-chart-2" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("goals.remaining")}</p>
                        <p className="text-xl font-bold tabular-nums font-mono">
                          {formatCurrency(debtProgress.sisaUtang)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Overall Progress */}
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{t("goals.overallProgress")}</span>
                      <span className="text-lg font-bold tabular-nums font-mono">{debtProgress.progressPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-chart-2 transition-all rounded-full"
                        style={{ width: `${Math.min(debtProgress.progressPercentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Debt Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    {t("goals.debtBreakdown")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {debtProgress.byDebt.map((debt) => (
                      <div
                        key={debt.debtId}
                        className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold">{debt.category}</p>
                            <p className="text-sm text-muted-foreground">
                              {t("goals.original")}: {formatCurrency(debt.originalAmount)}
                            </p>
                          </div>
                          <span className="text-lg font-bold tabular-nums font-mono">{debt.progressPercentage.toFixed(1)}%</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-3 p-2 rounded-lg bg-muted/50">
                          <div>
                            <p className="text-xs text-muted-foreground">Original</p>
                            <p className="text-sm font-medium tabular-nums font-mono">{formatCurrency(debt.originalAmount)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Current</p>
                            <p className="text-sm font-medium tabular-nums font-mono">{formatCurrency(debt.currentAmount)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Paid</p>
                            <p className="text-sm font-medium tabular-nums font-mono text-primary">{formatCurrency(debt.paid)}</p>
                          </div>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all rounded-full"
                            style={{ width: `${Math.min(debt.progressPercentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <CreditCard className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">{t("netWorth.noDebts")}</p>
                  <p className="text-sm text-muted-foreground mt-1">Add debts in Net Worth to track your reduction progress</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Net Worth Growth Tab */}
        <TabsContent value="net-worth">
          <div className="space-y-4">
            {/* Set Target Button */}
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  if (netWorthTarget) {
                    setNetWorthForm({
                      targetAmount: netWorthTarget.targetAmount.toString(),
                      targetDate: new Date(netWorthTarget.targetDate).toISOString().slice(0, 10),
                    });
                  } else {
                    const today = new Date();
                    const oneYearLater = new Date(today);
                    oneYearLater.setFullYear(today.getFullYear() + 1);
                    setNetWorthForm({
                      targetAmount: netWorthData?.current ? (netWorthData.current * 1.2).toFixed(2) : "",
                      targetDate: oneYearLater.toISOString().slice(0, 10),
                    });
                  }
                  setShowNetWorthForm(!showNetWorthForm);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {netWorthTarget ? t("goals.updateTarget") : t("goals.setTarget")}
              </Button>
            </div>

            {/* Target Form */}
            {showNetWorthForm && (
              <Card>
                <CardContent className="pt-6">
                  <form onSubmit={handleNetWorthTargetSubmit} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="net-worth-target">{t("goals.targetNetWorth")}</Label>
                        <Input
                          id="net-worth-target"
                          type="number"
                          step="0.01"
                          value={netWorthForm.targetAmount}
                          onChange={(e) => setNetWorthForm({ ...netWorthForm, targetAmount: e.target.value })}
                          placeholder="0.00"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="net-worth-date">{t("goals.targetDate")}</Label>
                        <Input
                          id="net-worth-date"
                          type="date"
                          value={netWorthForm.targetDate}
                          onChange={(e) => setNetWorthForm({ ...netWorthForm, targetDate: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit">
                        {netWorthTarget ? t("goals.updateTarget") : t("goals.setTarget")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowNetWorthForm(false)}
                      >
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {netWorthData ? (
              <>
                {/* Current vs Target Comparison */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-chart-3/10 flex items-center justify-center shrink-0">
                          <TrendingUp className="h-5 w-5 text-chart-3" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("goals.currentNetWorth")}</p>
                          <p className="text-2xl font-bold tabular-nums font-mono">
                            {formatCurrency(netWorthData.current)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  {netWorthTarget && (
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <Target className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("goals.targetNetWorth")}</p>
                            <p className="text-2xl font-bold text-primary tabular-nums font-mono">
                              {formatCurrency(netWorthTarget.targetAmount)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {formatDate(netWorthTarget.targetDate)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Progress */}
                {netWorthTarget && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{t("goals.progress")}</span>
                          <span className="text-lg font-bold tabular-nums font-mono">
                            {netWorthData.current >= netWorthTarget.targetAmount
                              ? "100.0%"
                              : ((netWorthData.current / netWorthTarget.targetAmount) * 100).toFixed(1) + "%"}
                          </span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all rounded-full"
                            style={{
                              width: `${Math.min(
                                (netWorthData.current / netWorthTarget.targetAmount) * 100,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/50">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">{t("goals.remaining")}</p>
                            <p className="font-semibold tabular-nums font-mono">
                              {formatCurrency(Math.max(0, netWorthTarget.targetAmount - netWorthData.current))}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">{t("goals.daysRemaining")}</p>
                            <p className="font-semibold tabular-nums font-mono">
                              {Math.ceil(
                                (new Date(netWorthTarget.targetDate).getTime() - new Date().getTime()) /
                                  (1000 * 60 * 60 * 24)
                              )} days
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* No Target */}
                {!netWorthTarget && (
                  <Card>
                    <CardContent className="py-12">
                      <div className="text-center">
                        <Target className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                        <p className="text-muted-foreground">{t("goals.noNetWorthTarget")}</p>
                        <p className="text-sm text-muted-foreground mt-1">{t("goals.clickSetTarget")}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-3" />
                    <p className="text-muted-foreground">{t("common.loading")}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
    </>
  );
}
